import React, { useState, useEffect } from 'react';
import { fetchWithAuth } from '../utils/fetchWithAuth';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Button,
  CircularProgress,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Card,
  CardContent,
  Grid,
  Tabs,
  Tab,
  Alert,
  Tooltip,
  Collapse,
  List,
  ListItem,
  ListItemIcon,
  Divider,
  FormControlLabel,
  Switch,
  Menu
} from '@mui/material';
import {
  Person as PersonIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Search as SearchIcon,
  LocationOn as LocationIcon,
  History as HistoryIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Close as CloseIcon,
  Badge as BadgeIcon,
  Email as EmailIcon,
  Login as LoginIcon,
  Assignment as AssignmentIcon,
  Sync as SyncIcon,
  CloudSync as CloudSyncIcon,
  Archive as ArchiveIcon,
  Unarchive as UnarchiveIcon,
  PersonOff as PersonOffIcon,
  MoreVert as MoreVertIcon
} from '@mui/icons-material';

const EmployeesPanel = () => {
  const [employees, setEmployees] = useState([]);
  const [locations, setLocations] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionMenuAnchor, setActionMenuAnchor] = useState(null);
  const [actionMenuEmployee, setActionMenuEmployee] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterLocation, setFilterLocation] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [showInactive, setShowInactive] = useState(false);

  // Dialog states
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);

  // Form states
  const [editForm, setEditForm] = useState({
    name: '',
    email: '',
    position: '',
    role_id: '',
    location_ids: []
  });

  const [newEmployeeForm, setNewEmployeeForm] = useState({
    name: '',
    email: '',
    position: '',
    location_ids: [],
    core_role: 'forms.manager'
  });

  // Details tab states
  const [detailsTab, setDetailsTab] = useState(0);
  const [loginHistory, setLoginHistory] = useState([]);
  const [employeeChanges, setEmployeeChanges] = useState([]);
  const [loadingDetails, setLoadingDetails] = useState(false);

  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      const [employeesRes, locationsRes, rolesRes] = await Promise.all([
        fetchWithAuth('/api/employees'),
        fetchWithAuth('/api/locations'),
        fetchWithAuth('/api/roles')
      ]);

      if (employeesRes.ok) {
        const data = await employeesRes.json();
        setEmployees(data);
      }

      if (locationsRes.ok) {
        const data = await locationsRes.json();
        setLocations(data);
      }

      if (rolesRes.ok) {
        const data = await rolesRes.json();
        setRoles(data);
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load employee data');
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (employee) => {
    setSelectedEmployee(employee);
    setEditForm({
      name: employee.name || '',
      email: employee.email || '',
      position: employee.position || '',
      role_id: employee.role_id || '',
      location_ids: employee.location_ids || []
    });
    setEditDialogOpen(true);
  };

  const handleViewDetails = async (employee) => {
    setSelectedEmployee(employee);
    setDetailsDialogOpen(true);
    setDetailsTab(0);
    setLoadingDetails(true);

    try {
      const [historyRes, changesRes] = await Promise.all([
        fetchWithAuth(`/api/employees/${employee.id}/login-history`),
        fetchWithAuth(`/api/employees/${employee.id}/changes`)
      ]);

      if (historyRes.ok) {
        const data = await historyRes.json();
        setLoginHistory(data);
      }

      if (changesRes.ok) {
        const data = await changesRes.json();
        setEmployeeChanges(data);
      }
    } catch (err) {
      console.error('Error fetching employee details:', err);
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleSaveEdit = async () => {
    try {
      const response = await fetchWithAuth(`/api/employees/${selectedEmployee.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editForm)
      });

      if (response.ok) {
        setEditDialogOpen(false);
        fetchInitialData();
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to update employee');
      }
    } catch (err) {
      console.error('Error updating employee:', err);
      setError('Failed to update employee');
    }
  };

  const handleAddEmployee = async () => {
    try {
      const response = await fetchWithAuth('/api/employees', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...newEmployeeForm,
          sync_to_core: true  // Always sync to Dailey Core
        })
      });

      if (response.ok) {
        setAddDialogOpen(false);
        setNewEmployeeForm({
          name: '',
          email: '',
          position: '',
          location_ids: [],
          core_role: 'forms.manager'
        });
        fetchInitialData();
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to add employee');
      }
    } catch (err) {
      console.error('Error adding employee:', err);
      setError('Failed to add employee');
    }
  };

  const handleArchiveEmployee = async (employee) => {
    if (!window.confirm(`Are you sure you want to archive ${employee.name}? They will no longer be able to log in.`)) {
      return;
    }

    try {
      const response = await fetchWithAuth(`/api/employees/${employee.id}/deactivate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: 'Archived by admin' })
      });

      if (response.ok) {
        fetchInitialData();
        setDetailsDialogOpen(false);
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to archive employee');
      }
    } catch (err) {
      console.error('Error archiving employee:', err);
      setError('Failed to archive employee');
    }
  };

  const handleReactivateEmployee = async (employee) => {
    if (!window.confirm(`Are you sure you want to reactivate ${employee.name}? They will be able to log in again.`)) {
      return;
    }

    try {
      const response = await fetchWithAuth(`/api/employees/${employee.id}/reactivate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });

      if (response.ok) {
        fetchInitialData();
        setDetailsDialogOpen(false);
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to reactivate employee');
      }
    } catch (err) {
      console.error('Error reactivating employee:', err);
      setError('Failed to reactivate employee');
    }
  };

  // Filter employees based on search and filters
  const filteredEmployees = employees.filter(emp => {
    const matchesSearch =
      emp.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      emp.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      emp.position?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesLocation = !filterLocation || emp.location_ids?.includes(parseInt(filterLocation));
    const matchesRole = !filterRole || emp.role_name === filterRole;
    const matchesActiveStatus = showInactive || emp.is_active !== 0;

    return matchesSearch && matchesLocation && matchesRole && matchesActiveStatus;
  });

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PersonIcon /> Employee Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setAddDialogOpen(true)}
        >
          Add Employee
        </Button>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search employees..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'action.active' }} />
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Filter by Location</InputLabel>
                <Select
                  value={filterLocation}
                  onChange={(e) => setFilterLocation(e.target.value)}
                  label="Filter by Location"
                >
                  <MenuItem value="">All Locations</MenuItem>
                  {locations.map(loc => (
                    <MenuItem key={loc.id} value={loc.id}>{loc.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Filter by Role</InputLabel>
                <Select
                  value={filterRole}
                  onChange={(e) => setFilterRole(e.target.value)}
                  label="Filter by Role"
                >
                  <MenuItem value="">All Roles</MenuItem>
                  {roles.map(role => (
                    <MenuItem key={role.id} value={role.role_name}>{role.role_name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControlLabel
                control={
                  <Switch
                    checked={showInactive}
                    onChange={(e) => setShowInactive(e.target.checked)}
                    size="small"
                  />
                }
                label={<Typography variant="body2">Show Archived</Typography>}
              />
            </Grid>
          </Grid>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {filteredEmployees.length} employees {showInactive && `(including archived)`}
          </Typography>
        </CardContent>
      </Card>

      {/* Employees Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: 'primary.main' }}>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Name</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Email</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Position</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Role</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Locations</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Last Login</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredEmployees.map((employee) => {
              const isArchived = employee.is_active === 0;
              return (
                <TableRow
                  key={employee.id}
                  hover
                  sx={isArchived ? { bgcolor: 'action.disabledBackground', opacity: 0.7 } : {}}
                >
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {isArchived ? (
                        <PersonOffIcon fontSize="small" color="disabled" />
                      ) : (
                        <PersonIcon fontSize="small" color="action" />
                      )}
                      <Typography
                        variant="body2"
                        fontWeight="medium"
                        sx={isArchived ? { textDecoration: 'line-through', color: 'text.disabled' } : {}}
                      >
                        {employee.name}
                      </Typography>
                      {isArchived && (
                        <Chip label="Archived" size="small" color="default" variant="outlined" />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color={isArchived ? 'text.disabled' : 'inherit'}>
                      {employee.email || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color={isArchived ? 'text.disabled' : 'inherit'}>
                      {employee.position || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {employee.role_name ? (
                      <Chip
                        label={employee.role_name}
                        size="small"
                        color={isArchived ? 'default' : (employee.role_name.toLowerCase().includes('admin') ? 'primary' : 'default')}
                        variant={isArchived ? 'outlined' : 'filled'}
                      />
                    ) : '-'}
                  </TableCell>
                  <TableCell>
                    {employee.assigned_locations ? (
                      <Tooltip title={employee.assigned_locations}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <LocationIcon fontSize="small" color={isArchived ? 'disabled' : 'action'} />
                          <Typography variant="body2" noWrap sx={{ maxWidth: 150 }} color={isArchived ? 'text.disabled' : 'inherit'}>
                            {employee.assigned_locations}
                          </Typography>
                        </Box>
                      </Tooltip>
                    ) : '-'}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {formatDate(employee.last_login)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        setActionMenuAnchor(e.currentTarget);
                        setActionMenuEmployee(employee);
                      }}
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Action Menu */}
      <Menu
        anchorEl={actionMenuAnchor}
        open={Boolean(actionMenuAnchor)}
        onClose={() => {
          setActionMenuAnchor(null);
          setActionMenuEmployee(null);
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem
          onClick={() => {
            handleViewDetails(actionMenuEmployee);
            setActionMenuAnchor(null);
          }}
        >
          <ListItemIcon>
            <HistoryIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Details</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleEditClick(actionMenuEmployee);
            setActionMenuAnchor(null);
          }}
          disabled={actionMenuEmployee?.is_active === 0}
        >
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        <Divider />
        {actionMenuEmployee?.is_active === 0 ? (
          <MenuItem
            onClick={() => {
              handleReactivateEmployee(actionMenuEmployee);
              setActionMenuAnchor(null);
            }}
            sx={{ color: 'success.main' }}
          >
            <ListItemIcon>
              <UnarchiveIcon fontSize="small" color="success" />
            </ListItemIcon>
            <ListItemText>Reactivate</ListItemText>
          </MenuItem>
        ) : (
          <MenuItem
            onClick={() => {
              handleArchiveEmployee(actionMenuEmployee);
              setActionMenuAnchor(null);
            }}
            sx={{ color: 'warning.main' }}
          >
            <ListItemIcon>
              <ArchiveIcon fontSize="small" color="warning" />
            </ListItemIcon>
            <ListItemText>Archive</ListItemText>
          </MenuItem>
        )}
      </Menu>

      {/* Edit Employee Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Edit Employee
          <IconButton
            onClick={() => setEditDialogOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Name"
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={editForm.email}
                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Position"
                value={editForm.position}
                onChange={(e) => setEditForm({ ...editForm, position: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Role</InputLabel>
                <Select
                  value={editForm.role_id}
                  onChange={(e) => setEditForm({ ...editForm, role_id: e.target.value })}
                  label="Role"
                >
                  <MenuItem value="">No Role</MenuItem>
                  {roles.map(role => (
                    <MenuItem key={role.id} value={role.id}>{role.role_name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Assigned Locations</InputLabel>
                <Select
                  multiple
                  value={editForm.location_ids}
                  onChange={(e) => setEditForm({ ...editForm, location_ids: e.target.value })}
                  input={<OutlinedInput label="Assigned Locations" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((id) => (
                        <Chip
                          key={id}
                          size="small"
                          label={locations.find(l => l.id === id)?.name || id}
                        />
                      ))}
                    </Box>
                  )}
                >
                  {locations.filter(l => l.name !== 'All').map(loc => (
                    <MenuItem key={loc.id} value={loc.id}>
                      <Checkbox checked={editForm.location_ids.includes(loc.id)} />
                      <ListItemText primary={loc.name} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveEdit}>Save Changes</Button>
        </DialogActions>
      </Dialog>

      {/* Add Employee Dialog */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Add New Employee
          <IconButton
            onClick={() => setAddDialogOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Name"
                required
                value={newEmployeeForm.name}
                onChange={(e) => setNewEmployeeForm({ ...newEmployeeForm, name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                required
                value={newEmployeeForm.email}
                onChange={(e) => setNewEmployeeForm({ ...newEmployeeForm, email: e.target.value })}
                helperText="Required for Dailey Core login"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Position"
                value={newEmployeeForm.position}
                onChange={(e) => setNewEmployeeForm({ ...newEmployeeForm, position: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Assigned Locations</InputLabel>
                <Select
                  multiple
                  value={newEmployeeForm.location_ids}
                  onChange={(e) => setNewEmployeeForm({ ...newEmployeeForm, location_ids: e.target.value })}
                  input={<OutlinedInput label="Assigned Locations" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((id) => (
                        <Chip
                          key={id}
                          size="small"
                          label={locations.find(l => l.id === id)?.name || id}
                        />
                      ))}
                    </Box>
                  )}
                >
                  {locations.filter(l => l.name !== 'All').map(loc => (
                    <MenuItem key={loc.id} value={loc.id}>
                      <Checkbox checked={newEmployeeForm.location_ids.includes(loc.id)} />
                      <ListItemText primary={loc.name} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <CloudSyncIcon color="primary" />
                <Typography variant="subtitle2">Dailey Core Integration</Typography>
              </Box>
              <FormControl fullWidth size="small">
                <InputLabel>Core Role</InputLabel>
                <Select
                  value={newEmployeeForm.core_role}
                  onChange={(e) => setNewEmployeeForm({ ...newEmployeeForm, core_role: e.target.value })}
                  label="Core Role"
                >
                  <MenuItem value="forms.admin">Forms Admin</MenuItem>
                  <MenuItem value="forms.manager">Forms Manager</MenuItem>
                  <MenuItem value="forms.viewer">Forms Viewer</MenuItem>
                </Select>
              </FormControl>
              <Alert severity="info" sx={{ mt: 2 }}>
                An invitation will be sent to the employee's email address to set up their login.
              </Alert>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAddEmployee}
            disabled={!newEmployeeForm.name.trim() || !newEmployeeForm.email.trim()}
            startIcon={<SyncIcon />}
          >
            Add & Sync to Core
          </Button>
        </DialogActions>
      </Dialog>

      {/* Employee Details Dialog */}
      <Dialog open={detailsDialogOpen} onClose={() => setDetailsDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PersonIcon />
            {selectedEmployee?.name}
          </Box>
          <IconButton
            onClick={() => setDetailsDialogOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Tabs value={detailsTab} onChange={(e, v) => setDetailsTab(v)} sx={{ mb: 2 }}>
            <Tab icon={<LoginIcon />} label="Login History" iconPosition="start" />
            <Tab icon={<AssignmentIcon />} label="Changes Made" iconPosition="start" />
          </Tabs>

          {loadingDetails ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {detailsTab === 0 && (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Timestamp</TableCell>
                        <TableCell>IP Address</TableCell>
                        <TableCell>User Agent</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {loginHistory.length > 0 ? (
                        loginHistory.map((entry) => (
                          <TableRow key={entry.id}>
                            <TableCell>{formatDate(entry.login_timestamp)}</TableCell>
                            <TableCell>{entry.ip_address || '-'}</TableCell>
                            <TableCell sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {entry.user_agent || '-'}
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={3} align="center">
                            <Typography color="text.secondary">No login history found</Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {detailsTab === 1 && (
                <List>
                  {employeeChanges.length > 0 ? (
                    employeeChanges.map((change) => (
                      <React.Fragment key={change.id}>
                        <ListItem>
                          <ListItemIcon>
                            <AssignmentIcon color="action" />
                          </ListItemIcon>
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {change.action}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {formatDate(change.timestamp)}
                              {change.individual_name && ` - ${change.individual_name}`}
                            </Typography>
                          </Box>
                        </ListItem>
                        <Divider component="li" />
                      </React.Fragment>
                    ))
                  ) : (
                    <ListItem>
                      <Typography color="text.secondary">No changes recorded</Typography>
                    </ListItem>
                  )}
                </List>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between' }}>
          <Box>
            {selectedEmployee?.is_active === 0 ? (
              <Button
                color="success"
                startIcon={<UnarchiveIcon />}
                onClick={() => handleReactivateEmployee(selectedEmployee)}
              >
                Reactivate Employee
              </Button>
            ) : (
              <Button
                color="warning"
                startIcon={<ArchiveIcon />}
                onClick={() => handleArchiveEmployee(selectedEmployee)}
              >
                Archive Employee
              </Button>
            )}
          </Box>
          <Button onClick={() => setDetailsDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EmployeesPanel;
